import { useState } from 'react';
import type { TaskType } from '@api';
import {
  TaskDetailsModal,
  TaskDetailsModalModeType,
  TaskFormSchemaType,
  TouringDataTable,
  useManageTableContext,
} from '@common';
import { usePermissionContext } from '@hypeit-gmbh/hypeit-ui-core';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import { useSubmitUpdateTask } from '../common';
import {
  useTaskAssignablesSuspense,
  useTasksOverviewTableColumns,
  useUpdateTaskStatus,
} from './hooks';

type TasksOverviewTablePropsType = {
  tasks: TaskType[];
  isPending: boolean;
};

export const TasksOverviewTable: React.FC<TasksOverviewTablePropsType> = ({
  tasks,
  isPending,
}) => {
  const { t } = useTranslation('tasks');
  const { hasPermission } = usePermissionContext();
  const [selectedTask, setSelectedTask] = useState<TaskType | null>(null);
  const [mode, setMode] = useState<TaskDetailsModalModeType>('display');
  const [opened, { open, close }] = useDisclosure(false);
  const {
    tableArguments: {
      pagination,
      sorting,
      filtering: { filters, setFilter },
    },
  } = useManageTableContext();
  const { assignableUsers, assignableTeams } = useTaskAssignablesSuspense();
  const { submitUpdateTask } = useSubmitUpdateTask();
  const { handleClose: handleCloseTask, reopenTask } = useUpdateTaskStatus();

  const userCanWriteTasks = hasPermission({
    permission: 'TASKS_ADMIN',
    action: 'WRITE',
  });

  const handleView = (task: TaskType): void => {
    setSelectedTask(task);
    setMode('display');
    open();
  };

  const handleClose = (): void => {
    setSelectedTask(null);
    setMode('display');
    close();
  };

  const handleSubmit = async (data: TaskFormSchemaType): Promise<void> => {
    if (selectedTask === null) {
      return;
    }

    const updatedTask = await submitUpdateTask(selectedTask.id, data);
    setSelectedTask(updatedTask);
    setMode('display');
  };

  const { columns } = useTasksOverviewTableColumns({
    priorityFilter: filters.priority,
    setPriorityFilter: (value) => setFilter('priority', value),
    statusFilter: filters.status,
    setStatusFilter: (value) => setFilter('status', value),
    onView: handleView,
    onClose: handleCloseTask,
    onReopen: reopenTask,
    userCanWriteTasks,
  });

  return (
    <>
      <TouringDataTable<TaskType>
        columns={columns}
        records={tasks}
        {...pagination}
        {...sorting}
        fetching={isPending}
        noRecordsText={t('TABLE.EMPTY')}
      />
      {userCanWriteTasks ? (
        <TaskDetailsModal
          opened={opened}
          onClose={handleClose}
          task={selectedTask}
          canEdit
          mode={mode}
          onModeChange={setMode}
          onSubmit={handleSubmit}
          users={assignableUsers}
          teams={assignableTeams}
        />
      ) : (
        <TaskDetailsModal
          opened={opened}
          onClose={handleClose}
          task={selectedTask}
        />
      )}
    </>
  );
};
